/**
 * Build client-side preview windows matching Lambda generateAutoCuts positioning
 * (1 cut / 5s, clamp 3–30, 2s duration, 0.5s gap). Skips quality checks for speed.
 */

const CUT_DURATION_SEC = 2;
const MIN_GAP_SEC = 0.5;
const MIN_CUTS = 3;
const MAX_CUTS = 30;

function normalizeDurationSec(raw, fallbackSec) {
  let d = Number(raw);
  if (!Number.isFinite(d) || d <= 0) return fallbackSec;
  if (d > 600) d = d / 1000;
  return Math.max(d, 0.5);
}

export function computeLambdaLikeCutPositions(durationSec) {
  const duration = Math.max(Number(durationSec) || CUT_DURATION_SEC * 2, CUT_DURATION_SEC);
  const baseCuts = Math.ceil(duration / 5);
  const numCuts = Math.max(MIN_CUTS, Math.min(MAX_CUTS, baseCuts));
  const cutDuration = CUT_DURATION_SEC;
  const minGap = MIN_GAP_SEC;
  const cutPositions = [];
  const segmentDuration = Math.max(2, duration / numCuts);

  for (let i = 0; i < numCuts; i += 1) {
    const segmentStart = i * segmentDuration;
    const segmentEnd = Math.min((i + 1) * segmentDuration, duration - cutDuration);

    let startTime;
    if (segmentEnd <= segmentStart) {
      const remainingStart = Math.max(0, segmentStart);
      const remainingEnd = Math.max(remainingStart + cutDuration, duration - cutDuration);
      startTime = remainingStart + Math.random() * Math.max(0, remainingEnd - remainingStart);
    } else {
      startTime = segmentStart + Math.random() * (segmentEnd - segmentStart);
    }

    if (startTime + cutDuration > duration) break;

    if (cutPositions.length > 0) {
      const last = cutPositions[cutPositions.length - 1];
      const lastCutEnd = last.startTime + last.duration;
      if (startTime < lastCutEnd + minGap) {
        startTime = lastCutEnd + minGap;
        if (startTime + cutDuration > duration) break;
      }
    }

    cutPositions.push({ index: i, startTime, duration: cutDuration });
  }

  return cutPositions;
}

/**
 * @param {{ previewUrl?: string, uri?: string, duration?: number, fileName?: string, videoId?: string }[]} assets
 */
export function buildLocalRawMoments(assets) {
  const moments = [];
  (assets || []).forEach((asset, assetIndex) => {
    const previewUrl = asset.previewUrl || asset.uri;
    if (!previewUrl) return;
    const duration = normalizeDurationSec(asset.duration, CUT_DURATION_SEC * 3);
    const positions = computeLambdaLikeCutPositions(duration);
    positions.forEach((pos) => {
      const startSec = pos.startTime;
      const endSec = Math.min(startSec + pos.duration, duration);
      moments.push({
        clientId: `${asset.videoId || previewUrl || assetIndex}-m${pos.index}-${startSec.toFixed(2)}`,
        previewUrl,
        videoUri: previewUrl,
        startSec,
        endSec,
        duration: pos.duration,
        hidden: false,
        videoId: asset.videoId ? String(asset.videoId) : undefined,
        fileName: asset.fileName,
      });
    });
  });
  return moments;
}

export function toggleLocalMomentHidden(moments, clientId) {
  return (moments || []).map((m) =>
    m.clientId === clientId ? { ...m, hidden: !m.hidden } : m
  );
}

export function buildPendingCutWindowsByVideo(moments) {
  const byUri = new Map();
  (moments || []).forEach((m) => {
    if (!m || m.hidden) return;
    const key = String(m.videoUri || m.previewUrl || '');
    if (!key) return;
    if (!byUri.has(key)) {
      byUri.set(key, {
        videoUri: key,
        videoId: m.videoId ? String(m.videoId) : undefined,
        windows: [],
      });
    }
    const entry = byUri.get(key);
    if (m.videoId && !entry.videoId) entry.videoId = String(m.videoId);
    entry.windows.push({
      startTime: Number(m.startSec) || 0,
      duration:
        Number(m.duration) ||
        Math.max(0.5, (Number(m.endSec) || 0) - (Number(m.startSec) || 0)) ||
        CUT_DURATION_SEC,
    });
  });
  return Array.from(byUri.values()).filter((e) => e.windows.length > 0);
}

/** Probe HTML video duration from a blob/object URL. */
export function probeVideoDuration(url) {
  return new Promise((resolve) => {
    if (!url || typeof document === 'undefined') {
      resolve(12);
      return;
    }
    const video = document.createElement('video');
    video.preload = 'metadata';
    const done = (d) => {
      video.removeAttribute('src');
      video.load();
      resolve(Number.isFinite(d) && d > 0 ? d : 12);
    };
    video.onloadedmetadata = () => done(video.duration);
    video.onerror = () => done(12);
    video.src = url;
  });
}
